import styled from '@emotion/styled';
import { AnimationItem } from 'lottie-web';
import React, { useCallback } from 'react';
import { useDeviceVideo } from '../../utils/hooks/useDeviceVideo';
import useIsVisible from '../../utils/useIsVisible';

type LottiePlayerPros = {
  loop?: boolean;
  className?: string;
  desktopPath?: string;
  mobilePath?: string;
};

const Player = styled.div`
  width: 100%;
  height: 100%;
`;

export default function LottiePlayer({ desktopPath, mobilePath, className }: LottiePlayerPros) {
  const path = useDeviceVideo<string>({ landscapeVideo: desktopPath, portraitVideo: mobilePath });
  const lottiePlayer = React.useRef<HTMLDivElement>(null);
  const lottieInstance = React.useRef<AnimationItem | null>(null);
  const isVisibleRef = React.useRef(false);
  const { hasSupport } = useIsVisible({
    ref: lottiePlayer,
    onIntersecting: useCallback(({ isIntersecting }) => {
      isVisibleRef.current = isIntersecting;
      if (isIntersecting) {
        lottieInstance.current?.play();
      } else {
        lottieInstance.current?.pause();
      }
    }, []),
  });

  React.useEffect(() => {
    if (lottiePlayer.current) {
      import('lottie-web').then(({ default: lottie }) => {
        if (lottiePlayer.current) {
          lottieInstance.current?.destroy();
          lottieInstance.current = lottie.loadAnimation({
            container: lottiePlayer.current,
            renderer: 'svg',
            autoplay: !hasSupport || isVisibleRef.current,
            loop: true,
            path,
          });
        }
      });
    }
    return () => {
      lottieInstance.current?.destroy();
    };
  }, [path, hasSupport]);

  return <Player className={className} ref={lottiePlayer} />;
}
